import { obtenerMensajeError } from "utils/apiErrors";
import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Autocomplete,
  TextField,
  Select,
  MenuItem,
  //IconButton,
  Typography,
  Box,
} from "@mui/material";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import Grid from "@mui/material/Grid2";
import {
  //   AddAPhoto as AddAPhotoIcon,
  Edit as EditIcon,
  //   Delete as DeleteIcon,
} from "@mui/icons-material";

import SuiBox from "components/SuiBox";
import SuiButton from "components/SuiButton";
import SuiInput from "components/SuiInput";
import SuiTypography from "components/SuiTypography";

//API
import BucketApi from "api/bucketApi";
import SedesApi from "api/sedesApi";
import ProcesosApi from "api/procesosApi";

// Función para validar el formato básico del email
const isValidEmail = (email) => {
  const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  return emailRegex.test(email);
};

// Función para limpiar y validar el input de email en tiempo real
const validateEmailInput = (value) => {
  if (value !== null) {
    // Eliminar espacios en blanco al inicio y final
    let cleanValue = value.trim();
    // Convertir a minúsculas para consistencia
    cleanValue = cleanValue.toLowerCase();
    // Remover caracteres no permitidos en emails
    cleanValue = cleanValue.replace(/[^a-z0-9@._-]/g, "");
    return {
      value: cleanValue,
      isValid: isValidEmail(cleanValue),
      errorMessage: isValidEmail(cleanValue) ? "" : "Por favor ingrese un Email válido",
    };
  }
};

const EditSedeModal = ({ open, onClose, sede, onSuccess }) => {
  const [directores, setDirectores] = useState([]);
  const [coordinadores, setCoordinadores] = useState([]);
  const [nuevoDirector, setNuevoDirector] = useState(null);
  const [nuevoCoordinador, setNuevoCoordinador] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [logoUrl, setLogoUrl] = useState("");
  const [hasLogo, setHasLogo] = useState(false);
  const fileInputRef = useRef(null);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    nombre: "",
    sub_director: "",
    celular: "",
    email: "",
    direccion: "",
    descripcion: "",
    departamento: "",
    ciudad: "",
    responsable: {
      director: null,
      coordinador: null,
    },
    codigo: "",
    resolucion: "",
    direccion_camara: "",
    email_camara: "",
  });
  const FILE_NAME = "logo_sede.jpg";

  const fetchInitialData = async () => {
    try {
      const [directorRes, coordinadorRes] = await Promise.all([
        ProcesosApi.fetchResponsables({
          roles: ["DIRECTOR(A)"],
        }),
        ProcesosApi.fetchResponsables({
          roles: ["COORDINADOR(A)"],
        }),
      ]);
      setDirectores(directorRes.data || []);
      setCoordinadores(coordinadorRes.data || []);
    } catch (error) {
      console.error("Error al obtener los datos iniciales:", error);
      setError(obtenerMensajeError(error, "Error al cargar los datos iniciales."));
    }
  };

  useEffect(() => {
    if (sede) {
      fetchInitialData();
      setFormData({
        ...sede,
      });
      setNuevoDirector(sede.responsable_detalle?.director || null);
      setNuevoCoordinador(sede.responsable_detalle?.coordinador || null);
      if (sede.id) {
        checkLogo(sede.id);
      }
    } else {
      setFormData({
        nombre: "",
        sub_director: "",
        celular: "",
        email: "",
        direccion: "",
        descripcion: "",
        departamento: "",
        ciudad: "",
        responsable: {
          director: "",
          coordinador: "",
        },
        codigo: "",
        resolucion: "",
        direccion_camara: "",
        email_camara: "",
      });
      setNuevoDirector(null);
      setNuevoCoordinador(null);
    }
  }, [sede]);

  const checkLogo = async (sedeId) => {
    try {
      const response = await BucketApi.checkSedeFileExists(FILE_NAME, sedeId);
      setHasLogo(response.data.exists);
      if (response.data.exists) {
        fetchLogoUrl(sedeId);
      }
    } catch (error) {
      console.error("Error verificando logo:", error);
      setHasLogo(false);
    }
  };

  const fetchLogoUrl = async (sedeId) => {
    try {
      //const response = await BucketApi.getSedeFileUrl(FILE_NAME, sedeId);
      const response = await BucketApi.getSedeFileUrlFresh(FILE_NAME, sedeId);
      setLogoUrl(response.data.file_url);
    } catch (error) {
      console.error("Error obteniendo URL del logo:", error);
      setLogoUrl("");
    }
  };

  const handleSelectChange = (e) => {
    if (e.target.name === "ciudad") {
      setFormData((prev) => ({
        ...prev,
        ciudad: e.target.value,
      }));
    }
    if (e.target.name === "departamento") {
      setFormData((prev) => ({
        ...prev,
        departamento: e.target.value,
      }));
    }
  };

  const handleSelectResponsable = (e, value, reason, fieldName) => {
    setFormData((prev) => ({
      ...prev,
      responsable: {
        ...prev.responsable,
        [fieldName]: value.id, // Establece solo el campo específico sin afectar al otro
      },
    }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "nombre") {
      const cleanValue = value.replace(/[^a-záéíóúñA-ZÁÉÍÓÚÑ\s]/g, "");
      setFormData((prev) => ({ ...prev, [name]: cleanValue }));
      return;
    }
    if (name === "codigo") {
      if (value.length > 10) return; // Ignora si supera 10 caracteres
      setFormData((prev) => ({ ...prev, [name]: value }));
      return;
    }
    if (name === "email") {
      const result = validateEmailInput(value);
      setFormData((prev) => ({ ...prev, email: result.value }));
      setError(result.isValid ? null : result.errorMessage);
      return;
    }
    if (name === "email_camara") {
      const result = validateEmailInput(value);
      setFormData((prev) => ({ ...prev, email_camara: result.value }));
      setError(result.isValid ? null : result.errorMessage);
      return;
    }
    if (name === "celular") {
      const cleanValue = value.replace(/[^0-9]/g, "");
      setFormData((prev) => ({ ...prev, [name]: cleanValue }));
      return;
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const resetState = () => {
    setError(undefined);
    setLogoUrl("");
    setHasLogo(false);
    setNuevoDirector(null);
    setNuevoCoordinador(null);
    setFormData({
      nombre: "",
      sub_director: "",
      celular: "",
      email: "",
      direccion: "",
      descripcion: "",
      departamento: "",
      ciudad: "",
      responsable: {
        director: "",
        coordinador: "",
      },
      codigo: "",
      resolucion: "",
      direccion_camara: "",
      email_camara: "",
    });
  };

  const handleLogoChange = async (event) => {
    event.stopPropagation();
    const file = event.target.files[0];
    if (!file) return;
    // Validación de tamaño (ejemplo: 2MB máximo)
    if (file.size > 2 * 1024 * 1024) {
      setError("El logo no debe exceder 2MB");
      return;
    }
    try {
      // 1. Generar vista previa local
      const objectUrl = URL.createObjectURL(file);
      setLogoUrl(objectUrl);
      setHasLogo(true);
      setLogoFile(file);
      // 2. Limpiar el input para permitir reseleccionar el mismo archivo
      event.target.value = null;
    } catch (error) {
      console.error("Error al procesar el logo:", error);
      setError(obtenerMensajeError(error, "Error al cargar el logo seleccionado"));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.nombre.trim()) {
      setError("Debes registrar un Nombre para la Sede");
      return;
    }
    const result = validateEmailInput(formData.email);
    if (result?.errorMessage) {
      setError(result.errorMessage);
      return;
    }
    const resultEmailCamara = validateEmailInput(formData.email_camara);
    if (resultEmailCamara?.errorMessage) {
      setError(resultEmailCamara.errorMessage);
      return;
    }
    try {
      let newSede;
      if (sede) {
        newSede = await SedesApi.updateSede(sede.id, formData);
      } else {
        newSede = await SedesApi.createSede(formData);
      }
      // Subir logo solo si hay un archivo seleccionado y la sede se creó/actualizó
      if (logoFile && newSede?.data?.id) {
        try {
          // 1. Eliminar el logo anterior (si existe)
          await BucketApi.deleteSedeFile(FILE_NAME, newSede.data.id);
          // 2. Subir el nuevo logo con timestamp para evitar cache
          const uploadData = new FormData();
          uploadData.append("file", logoFile);
          uploadData.append("file_name", FILE_NAME);
          uploadData.append("file_type", "sede");
          uploadData.append("sede_id", newSede.data.id);
          await BucketApi.uploadFile(uploadData);
          // 3. Obtener y actualizar la URL con parámetro de cache busting
          //const response = await BucketApi.getSedeFileUrl(FILE_NAME, newSede.data.id);
          const response = await BucketApi.getSedeFileUrlFresh(FILE_NAME, newSede.data.id);
          setLogoUrl(`${response.data.file_url}`);
        } catch (uploadError) {
          console.error("Error al actualizar el logo:", uploadError);
          setError(obtenerMensajeError(uploadError, "El logo no se pudo actualizar completamente"));
        }
      }
      if (newSede) {
        resetState();
        onSuccess();
      }
    } catch (error) {
      console.error("Error al guardar los datos:", error);
      setError(obtenerMensajeError(error, "Error al guardar los datos de la Sede"));
    }
  };

  const hCClose = () => {
    //if (reason === "backdropClick" || reason === "escapeKeyDown") return;
    resetState();
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={hCClose}
      maxWidth="md"
      fullWidth
      disableEscapeKeyDown
      disableBackdropClick
    >
      <DialogTitle>{sede ? "Editar Sede" : "Nueva Sede"}</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
            <Grid size={12}>
              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}
            </Grid>
          </Grid>
          <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
            <Grid size={8}>
              <SuiTypography variant="h6" fontWeight="light">
                Nombre
              </SuiTypography>
              <SuiInput
                name="nombre"
                placeholder="Nombre"
                value={formData.nombre}
                onChange={handleChange}
                fullWidth
              />
            </Grid>
            <Grid size={4}>
              <SuiTypography variant="h6" fontWeight="light">
                No. Celular
              </SuiTypography>
              <SuiInput
                name="celular"
                placeholder="No. Celular"
                value={formData.celular}
                onChange={handleChange}
                fullWidth
              />
            </Grid>
          </Grid>
          <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
            <Grid size={4}>
              <SuiTypography variant="h6" fontWeight="light">
                Director
              </SuiTypography>
              <Autocomplete
                name="director"
                disablePortal
                options={directores}
                getOptionLabel={(option) => option?.nombre_completo || ""}
                //sx={{ mb: 3 }}
                renderInput={(params) => <TextField {...params} />}
                value={nuevoDirector}
                onChange={(e, value, reason) =>
                  handleSelectResponsable(e, value, reason, "director")
                }
              />
            </Grid>
            <Grid size={4}>
              <SuiTypography variant="h6" fontWeight="light">
                Subdirector
              </SuiTypography>
              <SuiInput
                name="sub_director"
                placeholder="Subdirector"
                value={formData.sub_director}
                onChange={handleChange}
                fullWidth
              />
            </Grid>
            <Grid size={4}>
              <SuiTypography variant="h6" fontWeight="light">
                Coordinador(a)
              </SuiTypography>
              <Autocomplete
                name="coordinador"
                disablePortal
                options={coordinadores}
                getOptionLabel={(option) => option?.nombre_completo || ""}
                //sx={{ mb: 3 }}
                renderInput={(params) => <TextField {...params} />}
                value={nuevoCoordinador}
                onChange={(e, value, reason) =>
                  handleSelectResponsable(e, value, reason, "coordinador")
                }
              />
            </Grid>
          </Grid>
          <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
            <Grid size={6}>
              <SuiTypography variant="h6" fontWeight="light">
                Código
              </SuiTypography>
              <SuiInput
                name="codigo"
                placeholder="Código"
                value={formData.codigo}
                onChange={handleChange}
                fullWidth
              />
            </Grid>
            <Grid size={6}>
              <SuiTypography variant="h6" fontWeight="light">
                Resolución
              </SuiTypography>
              <SuiInput
                name="resolucion"
                placeholder="Resolución"
                value={formData.resolucion}
                onChange={handleChange}
                fullWidth
              />
            </Grid>
          </Grid>
          <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
            <Grid size={3}>
              <SuiTypography variant="h6" fontWeight="light">
                Departamento
              </SuiTypography>
              <Select
                name="departamento"
                value={formData.departamento}
                onChange={handleSelectChange}
                fullWidth
                displayEmpty
              >
                <MenuItem value="">
                  <em>Seleccione una Departamento</em>
                </MenuItem>
                <MenuItem value="Cesar">Cesar</MenuItem>
                <MenuItem value="Cundinamarca">Cundinamarca</MenuItem>
                <MenuItem value="Valle Del Cauca">Valle Del Cauca</MenuItem>
              </Select>
            </Grid>
            <Grid size={3}>
              <SuiTypography variant="h6" fontWeight="light">
                Ciudad
              </SuiTypography>
              <Select
                name="ciudad"
                value={formData.ciudad}
                onChange={handleSelectChange}
                fullWidth
                displayEmpty
              >
                <MenuItem value="">
                  <em>Seleccione una ciudad</em>
                </MenuItem>
                <MenuItem value="Bogota">Bogota</MenuItem>
                <MenuItem value="Cali">Cali</MenuItem>
                <MenuItem value="Valledupar">Valledupar</MenuItem>
              </Select>
            </Grid>
            <Grid size={6}>
              <SuiTypography variant="h6" fontWeight="light">
                Correo Electrónico
              </SuiTypography>
              <SuiInput
                name="email"
                value={formData.email}
                onChange={handleChange}
                type="email"
                placeholder="Email"
              />
            </Grid>
          </Grid>
          <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
            <Grid size={6}>
              <SuiTypography variant="h6" fontWeight="light">
                Dirección Sede
              </SuiTypography>
              <SuiInput
                name="direccion"
                placeholder="Dirección"
                value={formData.direccion || ""}
                onChange={handleChange}
                multiline
                rows={2}
                fullWidth
              />
            </Grid>
            <Grid size={6}>
              <SuiTypography variant="h6" fontWeight="light">
                Descripción
              </SuiTypography>
              <SuiInput
                name="descripcion"
                placeholder="Descripción"
                value={formData.descripcion || ""}
                onChange={handleChange}
                multiline
                rows={2}
                fullWidth
              />
            </Grid>
          </Grid>
          <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
            <Grid size={6}>
              <SuiTypography variant="h6" fontWeight="light">
                Correo Electrónico Cámara Comercio
              </SuiTypography>
              <SuiInput
                name="email_camara"
                value={formData.email_camara}
                onChange={handleChange}
                type="email"
              />
            </Grid>
            <Grid size={6}>
              <SuiTypography variant="h6" fontWeight="light">
                Dirección Cámara Comercio
              </SuiTypography>
              <SuiInput
                name="direccion_camara"
                value={formData.direccion_camara || ""}
                onChange={handleChange}
                multiline
                rows={2}
                fullWidth
              />
            </Grid>
          </Grid>
          <Grid
            container
            rowSpacing={2}
            columnSpacing={{ xs: 2, sm: 2, md: 2 }}
            sx={{
              mt: 3, // Añade 24px de margen superior (3 * 8px) O usa un valor específico: marginTop: '20px'
            }}
          >
            <Grid size={4}>
              <SuiBox mb={2}>
                <SuiTypography variant="h6" fontWeight="medium">
                  Logo
                </SuiTypography>
              </SuiBox>
            </Grid>
            <Grid size={4}>
              <SuiBox mb={2}>
                <Box>
                  {hasLogo ? (
                    <img
                      src={logoUrl}
                      alt="Logo Sede"
                      style={{ width: "100%", maxWidth: "200px", height: "auto" }}
                    />
                  ) : (
                    <Box>
                      <Typography> </Typography>
                    </Box>
                  )}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    style={{ display: "none" }}
                    onChange={handleLogoChange}
                  />
                </Box>
              </SuiBox>
            </Grid>
            <Grid size={4}>
              <SuiBox
                mb={2}
                sx={{
                  display: "flex",
                  alignItems: "left",
                  justifyContent: "flex-start",
                  width: "100%", // Asegura que el contenedor use todo el espacio disponible
                }}
              >
                <SuiButton
                  onClick={() => fileInputRef.current.click()}
                  variant="contained"
                  size="medium"
                >
                  {hasLogo ? <EditIcon /> : <FileUploadIcon />}
                </SuiButton>
              </SuiBox>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <SuiButton onClick={hCClose} buttonColor="light">
            Cancelar
          </SuiButton>
          <SuiButton type="submit" variant="gradient" buttonColor="dark">
            {sede ? "Actualizar" : "Crear"}
          </SuiButton>
        </DialogActions>
      </form>
    </Dialog>
  );
};

EditSedeModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  sede: PropTypes.object,
  onSuccess: PropTypes.func.isRequired,
};

export default EditSedeModal;
